import React, { ReactElement, useMemo } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Link } from 'react-router-dom';
import { SimplePageTitle } from '@/components/layout/PageTitle';
import { ExternalAgreementStatus } from '@/types';
import ExternalAgreementsFilterableList from './ExternalAgreementsFilterableList';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import { useUnreadThreadCount } from '@/utils/hooks';
import { Tabs } from '@ygtechnologies/rubus-ui';

export type ExternalAgreementsPageTabId = 'main' | 'tasks' | 'messages';
interface TabSetup {
    id: ExternalAgreementsPageTabId;
    name: ReactElement;
    url: string;
    render: (options: { projectId: number; status: ExternalAgreementStatus | null }) => ReactElement;
    notificationCount?: number | null;
}

interface ExternalAgreementsPageProps {
    projectId: string | number;
    tabId: ExternalAgreementsPageTabId;
    status: string;
}

const ExternalAgreementsPage: React.FC<ExternalAgreementsPageProps> = ({ projectId, tabId, status }) => {
    const projectOverview = useSelector((state: IState) => state.seller.projectOverview);
    const tab: ExternalAgreementsPageTabId = tabId;
    const unreadMessages = useUnreadThreadCount();

    const parsedStatus =
        !status || status === 'all'
            ? null
            : (Object.keys(ExternalAgreementStatus).find(
                  (x) => x.toLowerCase() === status.toLowerCase()
              ) as ExternalAgreementStatus);

    const tabs: TabSetup[] = useMemo(
        () => [
            {
                id: 'main',
                url: 'external-agreements',
                name: <Trans>All external agreements</Trans>,
                render: (options) => <ExternalAgreementsFilterableList mode="normal" {...options} />,
            },
            {
                id: 'tasks',
                url: 'external-agreements/tasks',
                name: <Trans>My tasks</Trans>,
                render: (options) => <ExternalAgreementsFilterableList mode="tasks" {...options} />,
                notificationCount: projectOverview.tasks?.externalAgreements,
            },
            {
                id: 'messages',
                url: 'external-agreements/messages',
                name: <Trans>By last message</Trans>,
                render: (options) => <ExternalAgreementsFilterableList mode="messages" {...options} />,
                notificationCount: unreadMessages.ExternalAgreement,
            },
        ],
        [projectOverview, unreadMessages]
    );

    const activeTab = tabs.find((x) => x.id === tab);

    return (
        <>
            <SimplePageTitle title={t`External agreements`} />

            <Tabs.Container activeId={tab} variant="underline" sticky stickyClassName="stickyBleed">
                {tabs.map((t) => (
                    <Tabs.Tab
                        key={t.id}
                        id={t.id}
                        as={Link}
                        to={`/projects/${projectId}/${t.url}`}
                        label={t.name}
                        badge={t.notificationCount ?? undefined}
                    />
                ))}
                <Tabs.Content>{activeTab?.render({ projectId: +projectId, status: parsedStatus })}</Tabs.Content>
            </Tabs.Container>
        </>
    );
};

export default ExternalAgreementsPage;
